// React hooks for enhanced communication service
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from 'react';

import { communicationService } from "../services/communicationService";
import type {
  SendSmsRequest,
  SendBulkSmsRequest,
  SendEmailRequest,
  CheckAreaNumberAvailableParams,
  StartAreaNumberCheckoutParams,
  SubscribeToAreaCodesParams,
  PhoneNumbersResponse,
  PhoneNumberPricingResponse
} from "../services/communicationService";
import { errorToast, successToast } from "../services/helper";
import AgencyTokenManager from "../services/agencyTokenManager";

// Import React for hooks

// Helper to get current agency context for query keys
const getAgencyContext = () => {
  const agencyId = AgencyTokenManager.getCurrentAgencyId();
  return agencyId || 'no-agency';
};

// Query keys for communication-related queries
export const communicationQueryKeys = {
  all: ['communication'] as const,
  areaCodes: () => [...communicationQueryKeys.all, 'area-codes'] as const,
  phoneNumbers: (stateCode?: string) => [...communicationQueryKeys.all, 'phone-numbers', stateCode] as const,
  phoneNumbersPricing: (stateCode?: string, areaCode?: string) => [...communicationQueryKeys.all, 'phone-numbers-pricing', stateCode, areaCode] as const,
  areaCodeAvailability: (params: unknown) => [...communicationQueryKeys.all, 'availability', params] as const,
  areaCodeCost: (codes: string, state?: string) => [...communicationQueryKeys.all, 'cost', codes, state] as const,
  // CRITICAL: Include agency ID in stats query key to get different data per agency
  stats: () => [...communicationQueryKeys.all, 'stats', getAgencyContext()] as const,
};

// Hook to get area codes (legacy - maintained for backward compatibility)
export const useAreaCodes = () => {
  return useQuery({
    queryKey: communicationQueryKeys.areaCodes(),
    queryFn: () => communicationService.getAreaCodes(),
    staleTime: 30 * 60 * 1000, // 30 minutes (area codes don't change often)
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 2,
    retryDelay: 1000,
    refetchOnMount: true,
  });
};

// NEW: Hook to get phone numbers with states (database-backed)
export const usePhoneNumbers = (stateCode?: string, requireAuth: boolean = true) => {
  return useQuery({
    queryKey: communicationQueryKeys.phoneNumbers(stateCode),
    queryFn: () => communicationService.getPhoneNumbers(stateCode, requireAuth),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 2,
    retryDelay: 1000,
    refetchOnMount: true,
  });
};

// NEW: Hook to get area codes for signup (public endpoint, no auth)
export const useAreaCodesForSignup = () => {
  return useQuery({
    queryKey: [...communicationQueryKeys.all, 'area-codes-signup'],
    queryFn: () => communicationService.getAreaCodesForSignup(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 2,
    retryDelay: 1000,
    refetchOnMount: true,
  });
};

// NEW: Hook to get phone number pricing (database-backed)
export const usePhoneNumberPricing = (stateCode?: string, areaCode?: string, enabled = true, requireAuth: boolean = true) => {
  return useQuery({
    queryKey: communicationQueryKeys.phoneNumbersPricing(stateCode, areaCode),
    queryFn: () => communicationService.getPhoneNumberPricing(stateCode, areaCode, requireAuth),
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
    retry: 2,
    retryDelay: 1000,
    refetchOnMount: true,
  });
};

// Hook to check area code availability
export const useAreaCodeAvailability = (params: CheckAreaNumberAvailableParams, enabled = true) => {
  return useQuery({
    queryKey: communicationQueryKeys.areaCodeAvailability(params),
    queryFn: () => communicationService.checkAreaNumberAvailable(params),
    enabled: enabled && params.areaCodes.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: 1000,
    refetchOnMount: true,
  });
};

// Hook to get area code cost
export const useAreaCodeCost = (codes: string, stateName?: string, enabled = true) => {
  return useQuery({
    queryKey: communicationQueryKeys.areaCodeCost(codes, stateName),
    queryFn: () => communicationService.getAreaCodeCost(codes, stateName),
    enabled: enabled && codes.length > 0,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
    retry: 2,
    retryDelay: 1000,
    refetchOnMount: true,
  });
};

// Hook to get communication statistics
// Outbound-call breakdown by dialer (manual/power/multi-line). Separate from the
// core stats so it loads independently and never blocks the Dial Time Stats card.
export const useDialerBreakdown = (range?: { startDate?: string; endDate?: string }) => {
  return useQuery({
    queryKey: [...communicationQueryKeys.stats(), 'dialer-breakdown', range?.startDate ?? 'all', range?.endDate ?? 'all'],
    queryFn: () => communicationService.getDialerBreakdown(range),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  });
};

export const useCommunicationStats = (range?: { startDate?: string; endDate?: string }) => {
  return useQuery({
    // Range in the key: without it, switching the dashboard's period would re-serve the
    // previous window's cached numbers.
    queryKey: [...communicationQueryKeys.stats(), range?.startDate ?? 'all', range?.endDate ?? 'all'],
    queryFn: () => communicationService.getCommunicationStats(range),
    staleTime: 0, // Always consider data stale to refetch on mount for instant refresh
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry if we've failed too many times
      if (failureCount >= 2) return false;

      const errorMessage = error?.message || '';

      // Don't retry on client errors (4xx) including 404, 401, 403
      if (errorMessage.includes('401') ||
          errorMessage.includes('403') ||
          errorMessage.includes('404') ||
          errorMessage.includes('400')) {
        return false;
      }

      // Only retry on server errors (5xx) or network issues
      if (errorMessage.includes('500') ||
          errorMessage.includes('502') ||
          errorMessage.includes('503') ||
          errorMessage.includes('Network') ||
          errorMessage.includes('timeout')) {
        return true;
      }

      // Default: don't retry unknown errors to prevent infinite loops
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * (attemptIndex + 1), 5000), // Linear backoff with max 5s
    throwOnError: false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
};

// Hook to send SMS
export const useSendSms = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SendSmsRequest) => communicationService.sendSms(data),
    onSuccess: async (response) => {
      // Don't show toast here as service already shows it

      // Invalidate SMS queries to refresh history
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['lead-sms-history'] }),
        queryClient.invalidateQueries({ queryKey: ['sms-history'] }),
        queryClient.invalidateQueries({ queryKey: ['communication', 'sms'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-sms'] }),
      ]);
    },
    onError: (error) => {

      // Don't show toast here as service already shows it
    },
  });
};

// Hook to send bulk SMS
export const useSendBulkSms = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SendBulkSmsRequest) => communicationService.sendBulkSms(data),
    onSuccess: async (response) => {
      // Don't show toast here as service already shows it

      // Invalidate SMS queries to refresh history
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['lead-sms-history'] }),
        queryClient.invalidateQueries({ queryKey: ['sms-history'] }),
        queryClient.invalidateQueries({ queryKey: ['communication', 'sms'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-sms'] }),
      ]);
    },
    onError: (error) => {

      // Don't show toast here as service already shows it
    },
  });
};

// Hook to send SMS to multiple recipients (convenience hook)
export const useSendSmsToMultiple = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recipients,
      message,
      options
    }: {
      recipients: string[];
      message: string;
      options?: Partial<SendSmsRequest>
    }) => communicationService.sendSmsToMultipleRecipients(recipients, message, options),
    onSuccess: async (response) => {
      // Don't show toast here as service already shows it

      // Invalidate SMS queries to refresh history
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['lead-sms-history'] }),
        queryClient.invalidateQueries({ queryKey: ['sms-history'] }),
        queryClient.invalidateQueries({ queryKey: ['communication', 'sms'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-sms'] }),
      ]);
    },
    onError: (error) => {

      // Don't show toast here as service already shows it
    },
  });
};

// Hook to send email
export const useSendEmail = () => {
  return useMutation({
    mutationFn: (data: SendEmailRequest) => communicationService.sendEmail(data),
    onSuccess: (response) => {
      // Don't show toast here as service already shows it
    },
    onError: (error) => {

      // Don't show toast here as service already shows it
    },
  });
};

// Hook to start area number checkout
export const useStartAreaNumberCheckout = () => {
  return useMutation({
    mutationFn: (params: StartAreaNumberCheckoutParams) => 
      communicationService.startAreaNumberCheckout(params),
    onSuccess: (response) => {
      successToast("Checkout session created successfully");
    },
    onError: (error) => {

      // Don't show toast here as service already shows it
    },
  });
};

// Hook to subscribe to area codes
export const useSubscribeToAreaCodes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: SubscribeToAreaCodesParams) => 
      communicationService.subscribeToAreaCodes(params),
    onSuccess: async (response) => {
      // Invalidate area codes queries to refresh availability
      await queryClient.invalidateQueries({
        queryKey: communicationQueryKeys.areaCodes(),
      });
      
      // Invalidate availability queries
      await queryClient.invalidateQueries({
        queryKey: [...communicationQueryKeys.all, 'availability'],
      });

      // Don't show toast here as service already shows it
    },
    onError: (error) => {

      // Don't show toast here as service already shows it
    },
  });
};

// Hook for SMS with template support
export const useSendSmsWithTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      to,
      templateId,
      variables,
      options
    }: {
      to: string;
      templateId: string;
      variables?: Record<string, string>;
      options?: Partial<SendSmsRequest>
    }) => {
      const smsData: SendSmsRequest = {
        to,
        text: '', // Will be filled by template
        templateId,
        variables,
        ...options,
      };
      return communicationService.sendSms(smsData);
    },
    onSuccess: async (response) => {
      // Don't show toast here as service already shows it

      // Invalidate SMS queries to refresh history
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['lead-sms-history'] }),
        queryClient.invalidateQueries({ queryKey: ['sms-history'] }),
        queryClient.invalidateQueries({ queryKey: ['communication', 'sms'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-sms'] }),
      ]);
    },
    onError: (error) => {

      // Don't show toast here as service already shows it
    },
  });
};

// Hook for email with template support
export const useSendEmailWithTemplate = () => {
  return useMutation({
    mutationFn: ({ 
      to, 
      templateId, 
      variables, 
      options 
    }: { 
      to: string; 
      templateId: string; 
      variables?: Record<string, string>; 
      options?: Partial<SendEmailRequest> 
    }) => {
      const emailData: SendEmailRequest = {
        to,
        subject: '', // Will be filled by template
        body: '', // Will be filled by template
        templateId,
        variables,
        ...options,
      };
      return communicationService.sendEmail(emailData);
    },
    onSuccess: (response) => {
      // Don't show toast here as service already shows it
    },
    onError: (error) => {

      // Don't show toast here as service already shows it
    },
  });
};

// Hook for area code search with debouncing
export const useAreaCodeSearch = (searchTerm: string, delay = 300) => {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState(searchTerm);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, delay);

    return () => clearTimeout(timer);
  }, [searchTerm, delay]);

  const { data: areaCodes, ...queryResult } = useAreaCodes();

  const filteredAreaCodes = React.useMemo(() => {
    if (!areaCodes || !debouncedSearchTerm) return areaCodes;

    const filtered: Record<string, number[]> = {};
    const searchLower = debouncedSearchTerm.toLowerCase();

    for (const [state, codes] of Object.entries(areaCodes)) {
      // Filter by state name or area code
      if (state.toLowerCase().includes(searchLower)) {
        filtered[state] = codes;
      } else {
        const matchingCodes = codes.filter(code => 
          code.toString().includes(debouncedSearchTerm)
        );
        if (matchingCodes.length > 0) {
          filtered[state] = matchingCodes;
        }
      }
    }

    return filtered;
  }, [areaCodes, debouncedSearchTerm]);

  return {
    ...queryResult,
    data: filteredAreaCodes,
    searchTerm: debouncedSearchTerm,
  };
};

// Hook for communication history (placeholder for future implementation)
export const useCommunicationHistory = (type?: 'sms' | 'email', limit = 50) => {
  return useQuery({
    queryKey: [...communicationQueryKeys.all, 'history', type, limit],
    queryFn: async () => {
      // This would typically fetch from a history endpoint
      // For now, return empty array
      return [];
    },
    enabled: false, // Disable until backend endpoint is available
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: true,
  });
};